import { FC } from "react";
import {
  createStyles,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@material-ui/core";
import { InfoOutlined as InfoOutlinedIcon } from "@material-ui/icons";

import { ROIEquation, Tooltip } from "components";
import { EMPTY_VALUE_PLACEHOLDER } from "constants/index";
import {
  formatCurrencyAmount,
  formatNumber,
  formatPercentage,
} from "lib/format";
import { ResaleInfo } from "services/listings/types";
import theme from "theme";

import TotalYieldEquation from "./TotalYieldEquation";

const useStyles = makeStyles(() =>
  createStyles({
    table: {
      width: "100%",
      "& td": {
        padding: theme.spacing(0.625, 0),
      },
      "& tr td:first-of-type": {
        width: "75%",
        color: "#4f5763",
      },
      "& tr td:last-of-type": {
        width: "25%",
        fontWeight: theme.typography.fontWeightMedium,
      },
      "& tr:last-of-type td:first-of-type": {
        display: "flex",
        alignItems: "center",
      },
      "& svg": {
        marginLeft: theme.spacing(0.625),
      },
      "& .MuiTableCell-root": {
        borderBottom: "none",
        fontSize: "inherit",
        lineHeight: "inherit",
      },
    },
  }),
);

interface ROITableProps {
  resaleInfo: ResaleInfo;
}

const ROITable: FC<ROITableProps> = ({ resaleInfo }) => {
  const classes = useStyles();

  return (
    <Table className={classes.table} size="small">
      <TableBody>
        <TableRow>
          <TableCell>Purchased Price</TableCell>
          <TableCell>
            {formatCurrencyAmount(
              "USD",
              Number(resaleInfo.purchased_amount) +
                Number(resaleInfo.buyer_fee),
            )}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell>Royalties Received</TableCell>
          <TableCell>
            {formatCurrencyAmount("USD", resaleInfo.royalties_received)}
          </TableCell>
        </TableRow>
        {!!resaleInfo.sale_proceeds && (
          <TableRow>
            <TableCell>Sale Proceeds</TableCell>
            <TableCell>
              {formatCurrencyAmount("USD", resaleInfo.sale_proceeds)}
            </TableCell>
          </TableRow>
        )}
        <TableRow>
          <TableCell>Time Held</TableCell>
          <TableCell>
            {formatNumber(Number(resaleInfo.years_held))} Years
          </TableCell>
        </TableRow>
        {resaleInfo.sale_proceeds ? (
          <TableRow>
            <TableCell>
              Return on Investment{" "}
              <Tooltip title={<ROIEquation />}>
                <InfoOutlinedIcon fontSize="small" />
              </Tooltip>
            </TableCell>
            <TableCell>
              {resaleInfo.return_on_investment_percentage !== null
                ? formatPercentage(
                    Number(resaleInfo.return_on_investment_percentage),
                  )
                : EMPTY_VALUE_PLACEHOLDER}
            </TableCell>
          </TableRow>
        ) : (
          <TableRow>
            <TableCell>
              Total Yield
              <Tooltip
                title={
                  <TotalYieldEquation
                    showSaleProceeds={!!resaleInfo.sale_proceeds}
                  />
                }
              >
                <InfoOutlinedIcon fontSize="small" />
              </Tooltip>
            </TableCell>
            <TableCell>
              {resaleInfo.total_yield !== null
                ? formatPercentage(Number(resaleInfo.total_yield))
                : EMPTY_VALUE_PLACEHOLDER}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

export default ROITable;
